import { FC, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { Checklist } from '@eneco/components/ui/Checklist/Checklist';
import useDC from '@eneco/dc/useDC';
import { FlowError, Header, Layout, NextButton, Price } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import {
  Box,
  Card,
  Heading,
  Image,
  NotificationBox,
  RadioGroup,
  RadioTile,
  Stack,
  Text,
  TextLink,
} from '@eneco/sparky';
import { mapImage, usePlaceholderContent } from '@sitecore/common';
import { CrossSellRentalDeviceStepRendering, Fields } from '@sitecore/types/manual/CrossSellRentalDeviceStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { getOffer } from '../../../shared/helpers';
import { useFlowTracking , useFlowNavigation , useCachedOfferMachine } from '../../../shared/hooks';
import { Product } from '../../../shared/types';

interface FormFields {
  hasCrossSellRentalDevice: string;
}

const ProductCard: FC<{ product?: Product; isSME?: boolean; textualData?: Fields }> = ({
  product,
  isSME,
  textualData,
}) => {
  const productData = textualData?.products?.productsList?.find(
    p => p?.fields?.productProperties?.marketingProductCode.value === product?.code,
  );
  const illustration = mapImage(productData?.fields?.content?.image);

  const costsType = isSME ? 'vatExcluded' : 'vatIncluded';

  return (
    <Card elevation="S" overflow="hidden">
      <Image maxWidth="100%" height="auto" src={illustration.src ?? ''} alt={illustration.alt ?? ''} />
      <Box padding="6">
        <Stack gap="2">
          <Stack direction="row" alignX="justify" gap="2">
            <Stack.Item>
              <Text weight="bold">{productData?.fields?.content?.productTypeLabel?.value}</Text>
            </Stack.Item>

            <Stack.Item shrink={false}>
              <Price suffix={textualData?.products?.perMonthText?.value}>
                {product?.costsMonthly?.[costsType] ?? 0}
              </Price>
            </Stack.Item>
          </Stack>

          <Checklist>
            {productData?.fields?.content?.uspList?.value?.enum?.map(({ name, label }) => (
              <Checklist.Item key={name} gap="2" alignY="start">
                <RichText html={label} />
              </Checklist.Item>
            ))}
          </Checklist>
        </Stack>
      </Box>
    </Card>
  );
};

export const CrossSellRentalDeviceStep: FC = () => {
  const { businessUnit, label } = useDC();
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();
  const [multipleDeviceError, setMultipleDeviceError] = useState(false);
  const [availableProduct, setAvailableProduct] = useState<Product>();

  const {
    context: flowContext,
    context: { hasCrossSellRentalDevice, isSME },
  } = flowState;

  const { offerState, sendToOfferMachine } = useCachedOfferMachine(businessUnit, label);

  const offer = offerState?.context?.offer;

  const availableOffer = getOffer({ ...flowContext, hasCrossSellRentalDevice: true }, offer);

  const getAvailableProduct = () => {
    const availableProducts =
      offer?.products?.filter((p: { productType?: string }) => p.productType === 'huurapparaat') ?? [];

    if (availableProducts.length > 1) {
      setMultipleDeviceError(true);
      setValue('hasCrossSellRentalDevice', 'false');
      return;
    }

    const product = availableOffer?.products?.find(p => p.type === 'huurapparaat');
    if (product) {
      setAvailableProduct(product);
    }
  };

  const { CrossSellRentalDeviceStep: { fields: textualData } = {} } = usePlaceholderContent<{
    CrossSellRentalDeviceStep: CrossSellRentalDeviceStepRendering;
  }>();

  const formSchema = yup.object({
    hasCrossSellRentalDevice: yup
      .boolean()
      .required(textualData?.choice?.hasCrossSellRentalDeviceRadio?.value?.requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      hasCrossSellRentalDevice: hasCrossSellRentalDevice?.toString(),
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const { trackViewItem, trackAddToCart, trackRemoveFromCart } = useFlowTracking();

  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const [isNotFound, setIsNotFound] = useState(false);
  const [isNotAvailable, setIsNotAvailable] = useState(false);
  const [isNotAuthenticated, setIsNotAuthenticated] = useState(false);

  useEffect(() => {
    sendToOfferMachine({
      type: 'GET_OFFER',
      flowContext: { ...flowContext },
      allowDynamicOrHybridOnly: false,
    });
  }, []);

  useEffect(() => {
    if (!offerState.matches('IDLE')) {
      setIsLoading(offerState.matches('FETCHING'));
      setIsFailed(!offerState.matches('FETCHING') && !offerState.matches('SUCCESS'));
      setIsSuccessful(offerState.matches('SUCCESS'));

      setIsNotFound(offerState.matches('ERROR_NOT_FOUND'));
      setIsNotAvailable(offerState.matches('ERROR_NOT_AVAILABLE'));
      setIsNotAuthenticated(offerState.matches('ERROR_NOT_AUTHENTICATED'));

      if (offerState.matches('SUCCESS')) {
        if (availableOffer) trackViewItem(availableOffer, offer);
        getAvailableProduct();
      }
    }
  }, [offerState]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();

      if ((formData.hasCrossSellRentalDevice === 'true') !== hasCrossSellRentalDevice) {
        if (availableOffer && availableProduct) {
          const selectedOffer = availableOffer;
          selectedOffer.products = [availableProduct];

          if (formData.hasCrossSellRentalDevice === 'true') {
            trackAddToCart(selectedOffer, offer);
          } else if (hasCrossSellRentalDevice) {
            trackRemoveFromCart(selectedOffer, offer);
          }
        }
      }

      updateContextAndGoToNextStep({
        hasCrossSellRentalDevice: formData.hasCrossSellRentalDevice === 'true',
      });
    }
  }, [isSubmitSuccessful]);

  const getErrorNotification = () => {
    if (isNotAuthenticated) return textualData?.genericError?.errorNotAuthenticatedNotification;
    if (isNotAvailable) return textualData?.genericError?.errorNotAvailableNotification;
    if (isNotFound) return textualData?.genericError?.errorNotFoundNotification;

    return textualData?.genericError?.errorNotification;
  };

  const getErrorButtonLabel = () => {
    if (isNotAuthenticated) return textualData?.genericError?.loginButtonText;
    if (!isNotAvailable && !isNotFound) return textualData?.genericError?.tryAgainButtonText;

    return undefined;
  };

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit} isLoading={isLoading}>
      {isSuccessful && (
        <>
          <Header>
            <Header.Title>{textualData?.content?.title?.value}</Header.Title>
            {textualData?.content?.text?.value && !multipleDeviceError && (
              <Header.Text>{textualData.content.text.value}</Header.Text>
            )}
          </Header>

          {multipleDeviceError ? (
            <NotificationBox
              isAlert={false}
              title="Er zijn meerdere huurapparaten van Eneco op je adres aanwezig"
              text="We kunnen die nog niet online regelen. Bel daarom met onze klantenservice, dan maken we het voor je in orde. Bellen kan via het telefoonnummer 088-8955 000 (je betaalt je gebruikelijke belkosten). We zijn dagelijks van 08:00 tot 17:00 uur bereikbaar."
              variant="info"
            />
          ) : (
            <>
              {textualData?.content?.notification?.value?.content && (
                <NotificationBox
                  isAlert={false}
                  variant={textualData?.content?.notification?.value?.variant}
                  title={textualData?.content?.notification?.value?.title}
                  text={<RichText html={textualData?.content?.notification?.value?.content} />}
                />
              )}

              <TrackedDialog
                title={textualData?.content?.moreInfoDialog?.value?.title}
                trigger={
                  <TextLink emphasis="high">{textualData?.content?.moreInfoDialog?.value?.triggerText}</TextLink>
                }>
                <RichText html={textualData?.content?.moreInfoDialog?.value?.content} />
              </TrackedDialog>

              <ProductCard product={availableProduct} isSME={isSME} textualData={textualData} />

              <Box paddingTop="6">
                <Heading id="hasCrossSellRentalDeviceLabel" as="h3" size="XS">
                  {textualData?.choice?.hasCrossSellRentalDeviceRadio?.value?.label}
                </Heading>
              </Box>

              <Controller
                control={control}
                name="hasCrossSellRentalDevice"
                render={({ field: { onChange, value, name } }) => (
                  <RadioGroup
                    aria-labelledby="hasCrossSellRentalDeviceLabel"
                    name={name}
                    direction="column"
                    value={value}
                    onValueChange={onChange}
                    error={errors.hasCrossSellRentalDevice?.message}>
                    {textualData?.choice?.hasCrossSellRentalDeviceRadio?.value?.options?.map(option => (
                      <RadioTile key={option.label} value={option.name}>
                        {option.label}
                      </RadioTile>
                    ))}
                  </RadioGroup>
                )}
              />
            </>
          )}

          <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
        </>
      )}

      {isFailed && (
        <FlowError
          type={isNotAuthenticated ? 'AUTHENTIFICATION' : 'GENERIC'}
          alignY="center"
          textualData={{
            notification: getErrorNotification(),
            buttonLabel: getErrorButtonLabel(),
          }}
        />
      )}
    </Layout.Content>
  );
};
